import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A sticky "Add to cart" bar for product pages.
 *
 * Two modes, set via the `data-mode` attribute:
 *
 * - `direct` (single-variant products): the button forwards its click to the
 *   real product form button, so all existing add-to-cart logic runs once
 *   (variant, quantity, animation, cart drawer). Its look/label/disabled state
 *   are mirrored from the real button and refreshed on every variant change.
 *
 * - `choose` (multi-variant products): the button opens the theme's quick-add
 *   variant selector (handled declaratively by `quick-add-component`), so the
 *   customer picks the variant instead of adding the default one. This element
 *   only manages the bar's visibility in that mode.
 *
 * Visibility is driven by an IntersectionObserver that hides the bar while the
 * original button is on screen.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The sticky button.
 * @extends {Component<Refs>}
 */
class StickyAddToCart extends Component {
  // Note: no `requiredRefs`. In "choose" mode the button lives inside a nested
  // `quick-add-component`, so it is not one of this component's own refs.

  /** @type {HTMLElement | null} */
  #section = null;
  /** @type {HTMLButtonElement | null} */
  #realButton = null;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {string} */
  #mode = 'direct';

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section');
    if (!this.#section) return;

    this.#mode = this.dataset.mode || 'direct';

    // In "direct" mode, mirror the real button and keep it in sync with variants.
    if (this.#mode === 'direct') {
      this.#realButton = this.#section.querySelector(
        'product-form-component add-to-cart-component button[name="add"], product-form-component button[name="add"]'
      );
      this.#sync(this.#section);
      this.#section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    // Reveal the bar only while the original button is scrolled out of view.
    const target =
      this.#section.querySelector('product-form-component .product-form-buttons') ??
      this.#section.querySelector('product-form-component button[name="add"]');

    if (target) {
      this.#observer = new IntersectionObserver(
        ([entry]) => this.#setVisible(!entry.isIntersecting),
        // Reveal slightly before the real button scrolls fully into view.
        { rootMargin: '0px 0px -20% 0px' }
      );
      this.#observer.observe(target);
    } else {
      this.#setVisible(true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#observer?.disconnect();
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const source = event.detail?.data?.html ?? this.#section;
    this.#sync(source);
  };

  /**
   * Mirrors the real add-to-cart button (look, label and disabled state) so the
   * sticky button is identical to the one in the product form.
   * @param {ParentNode | null} root
   */
  #sync(root) {
    if (!root) return;

    const button = this.refs.button;
    if (!button) return;

    const sourceButton = root.querySelector(
      'product-form-component add-to-cart-component button[name="add"], product-form-component button[name="add"]'
    );
    if (sourceButton instanceof HTMLButtonElement) {
      button.className = `${sourceButton.className} sticky-atc__button`;
      button.innerHTML = sourceButton.innerHTML;
      button.disabled = sourceButton.disabled || sourceButton.hasAttribute('disabled');
    }
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  /**
   * Direct mode: reuse the original form by forwarding the click to the real
   * submit button. Re-queried if the cached reference was replaced by a morph.
   */
  forward() {
    if (!this.#realButton?.isConnected) {
      this.#realButton =
        this.#section?.querySelector(
          'product-form-component add-to-cart-component button[name="add"], product-form-component button[name="add"]'
        ) ?? null;
    }
    this.#realButton?.click();
  }
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCart);
}
